'use client'

import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '@/components/ui/carousel'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { useTranslations } from 'next-intl'
import Image from 'next/image'

interface TranslatedItem {
  title: string
  sub_title: string
  feature: { text: string }[]
}

const staticData = [
  {
    videoSrc: '',
    imageSrc: '/image/2026/06/24/ngan_lo_vet_mo_hoi.jpg',
    featureIcons: [
      '/image/October2025/mceclip1_86.png',
      '/image/October2025/mceclip4_84.png',
    ],
  },
  {
    videoSrc: '',
    imageSrc: '/image/2026/06/24/cach_nhiet_ngan_tia_uv.jpg',
    featureIcons: [
      '/image/October2025/mceclip1_51.png',
      '/image/October2025/mceclip5_53.png',
    ],
  },
  {
    videoSrc: '',
    imageSrc: '/image/2026/06/24/Frame_88067.png',
    featureIcons: [
      '/image/October2025/mceclip5_51.png',
      '/image/October2025/mceclip4_84.png',
    ],
  },
]

export function SustainableMaterials() {
  const t = useTranslations(
    'zero_mark_collection_hardcode.sustainable_materials'
  )
  const itemCount = Object.keys(t.raw('items')).length
  const translatedItems: TranslatedItem[] = Array.from(
    { length: itemCount },
    (_, i) => t.raw(`items.${i}`)
  )

  const sustainableData = translatedItems.map((item, index) => {
    const featureCount = Object.keys(item.feature).length
    const features = Array.from({ length: featureCount }, (_, j) => {
      return t.raw(`items.${index}.feature.${j}`)
    })

    return {
      videoSrc: staticData[index].videoSrc,
      imageSrc: staticData[index].imageSrc,
      title: item.title,
      sub_title: item.sub_title,
      feature: (features as { text: string }[]).map(
        (featureItem, featureIndex) => ({
          icon: staticData[index].featureIcons[featureIndex],
          text: featureItem.text,
        })
      ),
    }
  })

  return (
    <div className='w-full bg-neutral-900 py-12 text-white md:py-20'>
      <div className='mx-auto px-5 md:container'>
        <h2 className='mb-4 text-center text-2xl font-semibold uppercase md:mb-12 md:text-3xl 2xl:text-4xl'>
          {t.rich('title', {
            tm: (chunks) => <span className='font-sans'>{chunks}</span>,
          })}
        </h2>

        {/* Mobile Carousel */}
        <div className='md:hidden'>
          <Carousel opts={{ align: 'start', loop: true }} className='w-full'>
            <CarouselContent className='-ml-4'>
              {sustainableData.map((item, index) => (
                <CarouselItem
                  key={index}
                  className='basis-[85%] pl-4 sm:basis-1/2'
                >
                  <div className='p-1'>
                    <SustainableCard {...item} />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
        </div>

        {/* Desktop Grid */}
        <div className='hidden md:grid md:grid-cols-3 md:gap-8'>
          {sustainableData.map((item, index) => (
            <SustainableCard key={index} {...item} />
          ))}
        </div>
      </div>
    </div>
  )
}

interface SustainableCardProps {
  videoSrc?: string
  imageSrc?: string
  title: string
  sub_title: string
  feature: { icon: string; text: string }[]
}

function SustainableCard({
  videoSrc,
  imageSrc,
  title,
  sub_title,
  feature,
}: SustainableCardProps) {
  const t = useTranslations(
    'zero_mark_collection_hardcode.sustainable_materials'
  )
  return (
    <div className='bg-transparent'>
      <div className='relative mb-4 aspect-[450/331] w-full overflow-hidden rounded-lg'>
        {videoSrc ? (
          <video
            className='absolute left-0 top-0 h-full w-full object-cover'
            loop
            playsInline
            muted
            autoPlay
            aria-label={title}
          >
            <source src={videoSrc} type='video/mp4' />
          </video>
        ) : null}
        {imageSrc ? (
          <Image
            src={ImageHelper.getN7ImageUrl(imageSrc)}
            alt={title}
            width={600}
            height={350}
            className='absolute left-0 top-0 h-full w-full object-cover'
          />
        ) : null}
      </div>
      <h3 className='font-criteria text-[15px] font-medium uppercase md:text-lg'>
        {title}
      </h3>
      <p className='mb-4 text-sm text-neutral-400 md:text-base'>{sub_title}</p>
      {feature.length > 0 ? (
        <div className='mb-4 border-t border-neutral-600 pt-4'>
          <p className='mb-3 text-xs font-bold uppercase text-neutral-500'>
            {t('feature')}
          </p>
          <div className='space-y-2'>
            {feature.map((f, i) => (
              <div key={i} className='flex items-center gap-3'>
                <div className='h-6 w-6 md:h-8 md:w-8'>
                  <Image
                    src={ImageHelper.getImageUrl(f.icon)}
                    alt={f.text}
                    width={24}
                    height={24}
                    className='h-6 w-6 md:h-8 md:w-8'
                  />
                </div>
                <span className='text-sm text-neutral-50 md:text-base'>
                  {f.text}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
      {/* <div className='border-t border-gray-700 pt-4'>
        <p className='mb-3 text-xs font-bold uppercase text-gray-400'>
          Tính năng
        </p>
        <div className='flex items-center gap-3'>
          <span className='text-sm text-neutral-50 md:text-base'>
            {feature}
          </span>
        </div>
      </div> */}
    </div>
  )
}
